import { useEffect, useState } from "react";
import { Brain, Plus, Edit2, Trash2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["Discount", "Location", "Package", "Safety", "Amenity", "Sightseeing", "Food", "Booking", "Custom"];

const SEEDS = [
  { category: "Sightseeing", trigger: "photos, images, gallery, room pics", response: "Share both links: Website https://mbhomestay.in and Google Maps https://maps.app.goo.gl/uYmPYkbBiJLgd7WT6", priority: 5 },
  { category: "Discount", trigger: "Royal package weekday, best price on Royal", response: "Mention EXTRA up to 30% OFF on Royal Package for Monday-Thursday check-in, combined with long-stay discount for max savings", priority: 5 },
];

export default function Knowledge() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "Custom", trigger: "", response: "", priority: 1, active: true });

  const load = () => { api.get("/knowledge").then(r => setItems(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.trigger || !form.response) { toast.error("Trigger + Response required"); return; }
    try {
      await api.post("/knowledge", form);
      toast.success("Knowledge saved — AI updated instantly");
      setOpen(false);
      setForm({ category: "Custom", trigger: "", response: "", priority: 1, active: true });
      load();
    } catch { toast.error("Failed"); }
  };

  const toggle = async (it, v) => {
    try {
      await api.post("/knowledge", { ...it, active: v });
      load();
    } catch { toast.error("Failed to update"); load(); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this knowledge item?")) return;
    try {
      await api.delete(`/knowledge/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const edit = (it) => { setForm(it); setOpen(true); };

  const seedExample = async (s) => {
    try {
      await api.post("/knowledge", { ...s, active: true });
      toast.success("Example added");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl flex items-center gap-3"><Brain className="w-8 h-8 text-[#B84F3E]" strokeWidth={1.5}/>AI Knowledge Base</h1>
          <p className="text-[#8A7970] mt-1">Teach your bot new things. Changes go LIVE instantly — no redeploy needed.</p>
        </div>
        <Button onClick={() => { setForm({ category: "Custom", trigger: "", response: "", priority: 1, active: true }); setOpen(true); }} className="rounded-full bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="new-knowledge-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5}/> Teach the AI
        </Button>
      </div>

      {/* Help card */}
      <div className="mb-6 bg-[#FBE8DA] rounded-xl p-5 border border-[#E7C2A6]/40">
        <div className="flex gap-3">
          <GraduationCap className="w-6 h-6 text-[#B84F3E] shrink-0" strokeWidth={1.5}/>
          <div className="text-sm text-[#4A403B] leading-relaxed">
            <b>How to teach the AI:</b> Give a clear TRIGGER (what guest asks about) and a RESPONSE (what AI should say). Higher priority items get more attention.
            <div className="mt-2 space-y-1 text-xs">
              <div>✅ <b>Good trigger:</b> "airport pickup, cab from Kolkata, transport"</div>
              <div>✅ <b>Good response:</b> "We arrange airport pickup from Kolkata for ₹3,000 one-way. Please book 24hr in advance."</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick seed buttons if empty */}
      {items.length === 0 && (
        <div className="mb-6 bg-[#FDFBF7] rounded-xl p-5 border border-[#E7C2A6]/40">
          <p className="text-sm text-[#8A7970] mb-3">Empty? Try adding these quick examples:</p>
          <div className="flex flex-wrap gap-2">
            {SEEDS.map((s, i) => (
              <Button key={i} variant="outline" size="sm" onClick={() => seedExample(s)} data-testid={`seed-${i}`}>
                + {s.category}: {s.trigger.slice(0, 30)}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Items list */}
      <div className="space-y-3">
        {items.map(it => (
          <div key={it.id} className="bg-[#FDFBF7] rounded-xl p-5 border border-[#E7C2A6]/40" data-testid={`k-item-${it.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-[#F5F2EB] text-xs uppercase tracking-wider text-[#B84F3E]">{it.category}</span>
                  <span className="text-xs text-[#8A7970]">Priority: {it.priority}</span>
                </div>
                <div className="text-sm">
                  <div className="text-[#8A7970] text-xs uppercase mb-1">When guest asks about:</div>
                  <div className="font-medium mb-3">{it.trigger}</div>
                  <div className="text-[#8A7970] text-xs uppercase mb-1">AI should say:</div>
                  <div className="whitespace-pre-wrap">{it.response}</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Switch checked={it.active} onCheckedChange={(v) => toggle(it, v)} data-testid={`k-toggle-${it.id}`}/>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => edit(it)} data-testid={`k-edit-${it.id}`}><Edit2 className="w-3.5 h-3.5"/></Button>
                  <Button variant="outline" size="sm" onClick={() => del(it.id)} data-testid={`k-del-${it.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="knowledge-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{form.id ? "Edit" : "Teach"} the AI</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-[#8A7970]">Category</label>
              <select className="w-full h-10 rounded-md border border-[#E7C2A6] bg-white px-3" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="k-category">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#8A7970]">Trigger — WHEN guest asks about (keywords, comma-separated)</label>
              <Input value={form.trigger} onChange={(e) => setForm({...form, trigger: e.target.value})} placeholder="e.g., airport pickup, cab from Kolkata, transport" data-testid="k-trigger"/>
            </div>
            <div>
              <label className="text-xs text-[#8A7970]">Response — WHAT the AI should say</label>
              <Textarea rows={4} value={form.response} onChange={(e) => setForm({...form, response: e.target.value})} placeholder="Complete answer with facts, prices, links etc." data-testid="k-response"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#8A7970]">Priority (1-10)</label>
                <Input type="number" min={1} max={10} value={form.priority} onChange={(e) => setForm({...form, priority: parseInt(e.target.value) || 1})} data-testid="k-priority"/>
              </div>
              <div className="flex items-end gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({...form, active: v})} data-testid="k-active"/>
                <span className="text-sm">Active</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="k-save-btn">Save & Teach AI</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
